// ===== Packages =====

import React, {
    useMemo,
}                                               from 'react';
import {
    useNavigate,
    useLocation,
}                                               from 'react-router-dom';
import { ReactSVG }                             from 'react-svg';

// ===== Components =====

import { Transition }                           from '../Transition';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    PAGE_ROUTE,
    USER_ACTION_TYPE,
}                                               from '../../enums';

// ===== Services =====

import {
    recordUserAction,
    detectTouchDevice,
    isTestMode,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
} from '../../services';

// ===== Interfaces =====

import {
    ICartItem,
}                                               from '../../interfaces';

// ===== Images =====

import CartIcon                                 from '../../images/8-bit_cart.svg';

// ===== Constants =====

import {
    HOVER_TARGET_CLASSNAME,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    DIGITAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    DIGITAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    PHYSICAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    PHYSICAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    NAVIGATION_CART_BUTTON_ID,
    UPLOADING_MEDIA_DESTINATION_ID,
    BODY_FONT_SIZE,
    CART_BUTTON_HEIGHT_MULTIPLIER,
    DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
    DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
    PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
    PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
}                                               from '../../constants/generalConstants';
import CURSOR_SIGN                              from '../../constants/cursorSigns';

// ===== Styles =====

import {
    Container,
    CartIconContainer,
    CartBadge,
}                                               from './styles';
import { theme }                                from '../../themes/theme-context';

CartButton.defaultProps = {
    style: {},
    showLightBackground: true,
};
interface Props {
    style?: any,
    dark: boolean,
    showLightBackground?: boolean,
    userCartItems: ICartItem[] | undefined,
    currentSessionId: string | null,
    customCursorActive: boolean,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}
function CartButton({
    style,
    dark,
    showLightBackground = true,
    userCartItems,
    currentSessionId,
    customCursorActive,
    onCursorEnter,
    onCursorLeave,
}: Props): JSX.Element {
    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();

    // ===== Animation Constants =====

    const CART_BADGE_TRANSITION_DURATION = 150;
    const CART_ITEM_COUNT_TRANSITION_DURATION = 500;
    const useStripeTestMode = isTestMode();

    const onCartEnter = (e: React.MouseEvent | React.TouchEvent | React.SyntheticEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.cart,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onCartLeave = (e: React.MouseEvent | React.TouchEvent | React.SyntheticEvent): void => {
        onCursorLeave(e);
    };

    const onCartClick = async (e: React.MouseEvent): Promise<void> => {
        onCursorLeave(e);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.clickCartButton,
                sessionId: currentSessionId,
                payload: {
                    route: location.pathname,
                },
            });
        }
        navigate(
            `/${PAGE_ROUTE.cart}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    // ===== Memoization =====

    const PHYSICAL_BOOK_PRODUCT_ID = useMemo(() => (
        useStripeTestMode
            ? PHYSICAL_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : PHYSICAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID
    ), [useStripeTestMode]);
    const PHYSICAL_BOOK_PREORDER_PRODUCT_ID = useMemo(() => (
        useStripeTestMode
            ? PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID
            : PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID
    ), [useStripeTestMode]);
    const DIGITAL_BOOK_PRODUCT_ID = useMemo(() => (
        useStripeTestMode
            ? DIGITAL_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : DIGITAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID
    ), [useStripeTestMode]);
    const DIGITAL_BOOK_PREORDER_PRODUCT_ID = useMemo(() => (
        useStripeTestMode
            ? DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID
            : DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID
    ), [useStripeTestMode]);

    const numCartItems = useMemo(() => {
        let count = 0;
        let complimentaryWebBookQuantity = 0;
        if (userCartItems != null) {
            userCartItems.forEach((item: ICartItem) => {
                count += item.quantity;
                if (
                    item.productId === PHYSICAL_BOOK_PRODUCT_ID
                    || item.productId === PHYSICAL_BOOK_PREORDER_PRODUCT_ID
                    || item.productId === DIGITAL_BOOK_PRODUCT_ID
                    || item.productId === DIGITAL_BOOK_PREORDER_PRODUCT_ID
                ) {
                    complimentaryWebBookQuantity += item.quantity;
                }
            });

            if (complimentaryWebBookQuantity > 0) {
                // Add complimentary web-book quantity matching qualifying items.
                count += complimentaryWebBookQuantity;
            }
        }
        return count;
    }, [
        userCartItems,
    ]);

    return (
        <Container
            lightBackground={dark}
            showLightBackground={showLightBackground}
            style={style}
            id={UPLOADING_MEDIA_DESTINATION_ID(NAVIGATION_CART_BUTTON_ID)}
            customCursorActive={customCursorActive}
            data-cursor-surface={dark ? 'light' : undefined}
        >
            <CartIconContainer
                dark={dark}
                showLightBackground={showLightBackground}
                length={CART_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                detectTouchDevice={detectTouchDevice(document)}
                className={HOVER_TARGET_CLASSNAME}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e) => onCartEnter(e),
                    onMouseLeave: (e) => onCartLeave(e),
                })}
                {...getPressEventHandlers({ onMouseDown: onCartClick })}
            >
                <ReactSVG
                    src={CartIcon}
                />
            </CartIconContainer>
            <Transition
                in={numCartItems > 0}
                timeout={{
                    enter: CART_BADGE_TRANSITION_DURATION,
                    exit: CART_BADGE_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(state) => (
                    <CartBadge
                        count={numCartItems}
                        transitionDuration={CART_ITEM_COUNT_TRANSITION_DURATION}
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: 'left',
                                offset: 10,
                                duration: CART_BADGE_TRANSITION_DURATION,
                                easing: theme.motion.eagerEasing,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: 'left',
                                offset: 10,
                            })[state],
                        }}
                    />
                )}
            </Transition>
        </Container>
    );
}

export default CartButton;
